import { computed, inject, provide, reactive, ref } from "vue";
import axios from "axios";
import { clearCompanySettingsCache, setCompanySettingsCache } from "@/services/companySettings";
import {
  DEFAULT_FORM,
  buildPayload,
  detailsFromGstin,
  editableValues,
  errorCountBySection,
  normalizeField,
  setupProgress,
  snapshot,
  stateByCode,
  stateByName,
  validateForm,
} from "./companySettingsRules";

const CONTEXT_KEY = Symbol("companySettings");

/**
 * State and actions for the Company Settings page. Created once by the page and shared
 * with the section components through provide/inject, so sections never mutate props.
 */
export function provideCompanySettings(store) {
  const form = reactive({ ...DEFAULT_FORM });
  const saved = ref(snapshot(form));
  const loading = ref(true);
  const saving = ref(false);
  const logoBusy = ref(false);
  const touched = reactive({});
  const submitted = ref(false);

  const canEdit = computed(() => store.getters.hasPermission("settings.manage"));
  const errors = computed(() => validateForm(form));
  // Errors are shown once a field was touched or after the first save attempt.
  const shownErrors = computed(() =>
    Object.fromEntries(Object.entries(errors.value).filter(([field]) => submitted.value || touched[field]))
  );
  const errorCounts = computed(() => errorCountBySection(shownErrors.value));
  const isDirty = computed(() => snapshot(form) !== saved.value);
  const progress = computed(() => setupProgress(form, errors.value));

  const visibleError = (field) => shownErrors.value[field] || null;

  function applyServerData(data) {
    Object.assign(form, { ...DEFAULT_FORM, ...(data || {}) });
    for (const key of Object.keys(form)) {
      if (form[key] === null) form[key] = DEFAULT_FORM[key] ?? "";
    }
    // Match the state dropdown even if it was saved as "KERALA" or only by code.
    const state = stateByCode(form.StateCode) || stateByName(form.State);
    if (state) Object.assign(form, { State: state.name, StateCode: state.code });
    saved.value = snapshot(form);
    setCompanySettingsCache(data || {});
    store.commit("SET_COMPANY_SETTINGS", data || {});
  }

  async function load() {
    loading.value = true;
    try {
      const { data } = await axios.get("/v1/company-settings");
      applyServerData(data);
    } finally {
      loading.value = false;
    }
  }

  function update(field, value) {
    form[field] = normalizeField(field, value);
    touched[field] = true;
    if (field === "State") {
      const state = stateByName(value);
      form.StateCode = state ? state.code : "";
    }
    if (field === "GSTIN") {
      // A GSTIN tells us the state and the PAN; fill them when they are empty or clearly stale.
      const { state, pan } = detailsFromGstin(form.GSTIN);
      if (state && form.GSTIN.length === 15) {
        form.State = state.name;
        form.StateCode = state.code;
        if (pan && !form.PANNo) form.PANNo = pan;
      }
    }
    if (field === "GSTType" && value === "UNREGISTERED") touched.GSTIN = true;
  }

  function touch(field) {
    touched[field] = true;
  }

  /** Returns { ok, errors }. Invalid forms are not sent. */
  async function save() {
    submitted.value = true;
    if (Object.keys(errors.value).length) return { ok: false, errors: errors.value };
    saving.value = true;
    try {
      await axios.put("/v1/company-settings", buildPayload(form));
      const { data } = await axios.get("/v1/company-settings");
      applyServerData(data);
      submitted.value = false;
      Object.keys(touched).forEach((key) => delete touched[key]);
      return { ok: true };
    } finally {
      saving.value = false;
    }
  }

  function discard() {
    const pending = JSON.parse(saved.value);
    Object.assign(form, pending);
    submitted.value = false;
    Object.keys(touched).forEach((key) => delete touched[key]);
  }

  /** Server-side changes (logo, default location) must not wipe unsaved edits. */
  async function refreshKeepingEdits() {
    const edits = editableValues(form);
    const wasDirty = isDirty.value;
    const { data } = await axios.get("/v1/company-settings");
    applyServerData(data);
    if (wasDirty) Object.assign(form, edits);
  }

  async function uploadLogo(file) {
    logoBusy.value = true;
    try {
      const body = new FormData();
      body.append("file", file);
      await axios.post("/v1/company-settings/logo", body, { headers: { "Content-Type": "multipart/form-data" } });
      clearCompanySettingsCache();
      await refreshKeepingEdits();
    } finally {
      logoBusy.value = false;
    }
  }

  async function removeLogo() {
    logoBusy.value = true;
    try {
      await axios.delete("/v1/company-settings/logo");
      clearCompanySettingsCache();
      await refreshKeepingEdits();
    } finally {
      logoBusy.value = false;
    }
  }

  async function setDefaultLocation(locationid) {
    await axios.put("/v1/company-settings", { DefaultLocationId: locationid });
    await refreshKeepingEdits();
  }

  const context = {
    form,
    loading,
    saving,
    logoBusy,
    canEdit,
    errors,
    shownErrors,
    errorCounts,
    isDirty,
    progress,
    visibleError,
    load,
    update,
    touch,
    save,
    discard,
    uploadLogo,
    removeLogo,
    setDefaultLocation,
  };
  provide(CONTEXT_KEY, context);
  return context;
}

export function useCompanySettings() {
  const context = inject(CONTEXT_KEY, null);
  if (!context) throw new Error("useCompanySettings() must be used inside the Company Settings page");
  return context;
}
